/**
 * PWA Manager - Enhanced
 * Handles service worker registration and install prompt
 * Supports Chrome, Safari, Firefox, Samsung Internet, and desktop browsers
 *
 * Features:
 * - Browser-specific installation instructions
 * - Deep linking from Firefox to Chrome
 * - Post-installation detection and "Open in App" guidance
 * - Desktop QR code for mobile redirect
 * - No alert() calls - uses scene-based UI instead
 */

export interface BrowserInfo {
  name: 'chrome' | 'firefox' | 'safari' | 'samsung' | 'edge' | 'other';
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  isMobile: boolean;
  supportsBeforeInstallPrompt: boolean;
}

export type InstallAction =
  | { type: 'native_prompt' } // Chrome/Edge - show native prompt
  | { type: 'show_scene' } // All other browsers - show InstallPromptScene
  | { type: 'already_installed' }; // App is already installed

export class PWAManager {
  private deferredPrompt: any = null;
  private installListeners: Array<() => void> = [];
  private browserInfo: BrowserInfo;
  private promptResolvers: Array<() => void> = []; // Resolvers waiting for prompt event

  constructor() {
    this.browserInfo = this.detectBrowser();
    this.init();
  }

  private init(): void {
    // Note: Service worker registration is handled automatically by VitePWA
    // (see vite.config.ts with injectRegister: 'auto')

    // Listen for install prompt
    this.setupInstallPrompt();

    // Log service worker status
    this.logServiceWorkerStatus();
  }

  /**
   * Comprehensive browser detection
   */
  private detectBrowser(): BrowserInfo {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;

    // Platform detection
    // Note: Modern iPads (iPadOS 13+) request desktop sites by default and show "Macintosh" in UA
    // Detect iPad by checking for Mac with touch support
    const isIPadInDesktopMode = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    const isIOS = (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) || isIPadInDesktopMode;
    const isAndroid = /Android/.test(ua);
    const isMobile = isIOS || isAndroid || /Mobile|Tablet/.test(ua);
    const isDesktop = !isMobile;

    // Browser detection
    let browserName: BrowserInfo['name'] = 'other';

    if (/SamsungBrowser/i.test(ua)) {
      browserName = 'samsung';
    } else if (/Firefox/i.test(ua)) {
      browserName = 'firefox';
    } else if (isIOS && /Safari/i.test(ua) && !/Chrome/i.test(ua)) {
      browserName = 'safari';
    } else if (/Edg/i.test(ua)) {
      browserName = 'edge';
    } else if (/Chrome/i.test(ua) || /CriOS/i.test(ua)) {
      browserName = 'chrome';
    }

    // beforeinstallprompt support (mainly Chrome/Edge)
    const supportsBeforeInstallPrompt = browserName === 'chrome' || browserName === 'edge';

    return {
      name: browserName,
      isIOS,
      isAndroid,
      isDesktop,
      isMobile,
      supportsBeforeInstallPrompt,
    };
  }

  private logServiceWorkerStatus(): void {
    if ('serviceWorker' in navigator) {
      // Wait a bit for VitePWA to register the service worker
      setTimeout(() => {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          if (registrations.length > 0) {
            const firstReg = registrations[0];
            if (firstReg) {
              console.log('✅ Service Worker registered:', firstReg.scope);
            }
          } else {
            console.warn('⚠️ No service worker registered yet');
          }
        });
      }, 1000);
    }
  }

  private setupInstallPrompt(): void {
    // Check if beforeinstallprompt will fire
    setTimeout(() => {
      if (!this.deferredPrompt && this.browserInfo.supportsBeforeInstallPrompt) {
        console.warn('⚠️ beforeinstallprompt event did not fire - check manifest/service worker');
      }
    }, 5000);

    // Capture the install prompt event
    window.addEventListener('beforeinstallprompt', e => {
      console.log('✅ beforeinstallprompt event fired - PWA is installable');

      // Prevent the mini-infobar from appearing
      e.preventDefault();

      // Store the event for later use
      this.deferredPrompt = e;

      // Notify all waiting resolvers
      this.promptResolvers.forEach(resolve => resolve());
      this.promptResolvers = [];
    });

    // Track successful installations
    window.addEventListener('appinstalled', () => {
      console.log('✅ PWA installed successfully!');
      this.deferredPrompt = null;

      // Mark as installed in localStorage
      this.markAsInstalled();

      // Notify listeners
      this.installListeners.forEach(listener => listener());
    });
  }

  /**
   * Mark the app as installed in localStorage
   */
  private markAsInstalled(): void {
    try {
      localStorage.setItem('blobo_pwa_installed', 'true');
      localStorage.setItem('blobo_pwa_install_timestamp', Date.now().toString());
    } catch (error) {
      console.warn('Could not save installation status to localStorage:', error);
    }
  }

  /**
   * Check if app was previously installed
   */
  public wasInstalledBefore(): boolean {
    try {
      return localStorage.getItem('blobo_pwa_installed') === 'true';
    } catch (error) {
      return false;
    }
  }

  /**
   * Register a callback for when the app is installed
   */
  public onInstalled(callback: () => void): void {
    this.installListeners.push(callback);
  }

  /**
   * Prompt the native install dialog (Chrome/Edge only)
   */
  public async promptNativeInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!this.deferredPrompt) {
      console.warn('⚠️ Install prompt not available - deferredPrompt is null');
      return 'unavailable';
    }

    try {
      // Show the install prompt (can only be called once per event)
      this.deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await this.deferredPrompt.userChoice;

      // Clear the deferred prompt (it's now consumed)
      this.deferredPrompt = null;

      // Mark as installed if user accepted
      if (outcome === 'accepted') {
        this.markAsInstalled();
      }

      return outcome === 'accepted' ? 'accepted' : 'dismissed';
    } catch (error) {
      console.error('❌ Error showing install prompt:', error);
      // Clear the deferred prompt on error
      this.deferredPrompt = null;
      return 'unavailable';
    }
  }

  /**
   * Check if app is running as PWA
   */
  public isStandalone(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (window.navigator as any).standalone === true
    );
  }

  /**
   * Show install status in console
   */
  public logStatus(): void {
    console.log('📱 PWA Status:', {
      isStandalone: this.isStandalone(),
      hasServiceWorker: 'serviceWorker' in navigator,
      installPromptAvailable: !!this.deferredPrompt,
      browser: this.browserInfo,
    });
  }

  /**
   * Get browser information
   */
  public getBrowserInfo(): BrowserInfo {
    return this.browserInfo;
  }

  /**
   * Check if PWA install is available
   * Always returns true for mobile (show instructions), false only if already installed
   */
  public canInstall(): boolean {
    // Already installed
    if (this.isStandalone()) return false;

    // Show install button for all mobile users (we'll provide instructions for each browser)
    // Desktop users get QR code
    return true;
  }

  /**
   * Determine what action to take when user wants to install
   * Returns an action that the calling scene should handle
   */
  public getInstallAction(): InstallAction {
    // Already installed
    if (this.isStandalone()) {
      return { type: 'already_installed' };
    }

    // Chrome/Edge with native prompt available
    if (this.deferredPrompt) {
      return { type: 'native_prompt' };
    }

    // All other cases: show InstallPromptScene
    return { type: 'show_scene' };
  }

  /**
   * Poll for installation status (useful for detecting manual installations)
   */
  public startInstallationPolling(callback: (installed: boolean) => void): () => void {
    const checkInterval = setInterval(() => {
      if (this.isStandalone()) {
        clearInterval(checkInterval);
        callback(true);
      }
    }, 500); // Check every 500ms

    // Stop polling after 30 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 30000);

    // Return cleanup function
    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }

  /**
   * Generate QR code data URL for mobile redirect
   * Returns a promise that resolves to a QR code image data URL
   */
  public async generateQRCode(): Promise<string> {
    const url = 'https://blobo.app';

    // Use QR API service
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;

    return qrApiUrl;
  }

  /**
   * Try to open blobo.app in Chrome (Android only)
   * Returns true if attempt was made, false if not applicable
   */
  public tryOpenInChrome(): boolean {
    if (!this.browserInfo.isAndroid) {
      return false;
    }

    // Try Chrome intent URL (Android)
    // Format: intent://[host]/[path]#Intent;scheme=[scheme];package=[package];end
    const chromeIntent = 'intent://blobo.app/#Intent;scheme=https;package=com.android.chrome;end';

    try {
      // Use window.open to trigger intent without navigating current page away
      // This allows the button to work multiple times if user returns
      console.log('🔥 Opening Chrome via intent URL...');
      const newWindow = window.open(chromeIntent, '_blank');

      // Some browsers might block the popup, but intent URLs usually work
      if (newWindow) {
        // Intent was triggered successfully (or popup wasn't blocked)
        console.log('✅ Intent triggered via window.open');
      } else {
        // Fallback: try with location.href (but this navigates away)
        console.log('⚠️ window.open blocked or returned null, trying location.href fallback');
        window.location.href = chromeIntent;
      }

      return true;
    } catch (error) {
      console.error('Failed to open in Chrome:', error);
      return false;
    }
  }

  /**
   * Check if native install prompt is available
   */
  public hasNativePrompt(): boolean {
    return !!this.deferredPrompt;
  }

  /**
   * Wait for the native install prompt to become available
   * Returns a promise that resolves when prompt is ready or rejects after timeout
   */
  public async waitForInstallPrompt(timeoutMs: number = 8000): Promise<boolean> {
    const startTime = Date.now();
    console.log(`⏳ Waiting for beforeinstallprompt event (timeout: ${timeoutMs}ms)...`);

    // Already available
    if (this.deferredPrompt) {
      console.log('✅ Prompt already available immediately');
      return true;
    }

    // Not supported on this browser
    if (!this.browserInfo.supportsBeforeInstallPrompt) {
      console.warn('❌ Browser does not support beforeinstallprompt event');
      return false;
    }

    // Wait for the event to fire
    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        const elapsed = Date.now() - startTime;
        // Remove from resolvers if timeout
        const index = this.promptResolvers.indexOf(onPromptReady);
        if (index > -1) {
          this.promptResolvers.splice(index, 1);
        }
        console.warn(`⏱️ Timeout waiting for beforeinstallprompt event after ${elapsed}ms`);
        console.warn('💡 This usually means the manifest or service worker has an issue');
        resolve(false);
      }, timeoutMs);

      const onPromptReady = () => {
        const elapsed = Date.now() - startTime;
        console.log(`✅ Prompt became available after ${elapsed}ms`);
        clearTimeout(timeout);
        resolve(true);
      };

      this.promptResolvers.push(onPromptReady);
      console.log(`📋 Added to wait queue (${this.promptResolvers.length} waiting)`);
    });
  }
}
